import type { ComponentType, ReactNode } from "react";
import { Badge as MantineBadge } from "@mantine/core";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircle,
  IconCircleCheck,
  IconInfoCircle,
  type IconProps,
} from "@tabler/icons-react";

import { cn } from "@/lib/cn";

export type Tone = "ok" | "warn" | "danger" | "neutral" | "info";

const COLORS: Record<Tone, string> = {
  ok: "ok",
  warn: "warn",
  danger: "danger",
  neutral: "gray",
  info: "brand",
};

/**
 * Glyph per tone, so the badges stay distinguishable in greyscale and to a
 * colour-blind reader. Section 9 is explicit: never colour alone. The text
 * label does most of that work; the shape makes a column scannable (ADR-034).
 */
const TONE_ICONS: Record<Tone, ComponentType<IconProps>> = {
  ok: IconCircleCheck,
  warn: IconAlertTriangle,
  danger: IconAlertCircle,
  neutral: IconCircle,
  info: IconInfoCircle,
};

interface BadgeProps {
  tone: Tone;
  children: ReactNode;
  className?: string;
  /** Overrides the tone default when the domain needs a more specific mark. */
  icon?: ComponentType<IconProps>;
}

export function Badge({ tone, children, className, icon }: BadgeProps) {
  const IconComponent = icon ?? TONE_ICONS[tone];

  return (
    <MantineBadge
      color={COLORS[tone]}
      variant="light"
      size="md"
      className={cn("normal-case tracking-normal", className)}
      leftSection={<IconComponent size={18} stroke={1.75} aria-hidden />}
    >
      {children}
    </MantineBadge>
  );
}
